import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, errMsg, useApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PrivacySettings, DpaCard } from "@/components/SettingsExtra";
import { CalendarSection } from "@/components/GcalSettings";
import { AiToggle, AccountData } from "@/components/AccountPrivacy";
import { DsrRegistry } from "@/components/DsrRegistry";
import { PromoLog } from "@/components/PromoLog";

const BDAY = "Tanti auguri di buon compleanno, {nome}! Le auguro una giornata serena e un anno pieno di soddisfazioni. Resto sempre a disposizione per qualsiasi esigenza.";

const Automations = ({ f, setF, save }) => {
  const toggle = (k) => (v) => { const n = { ...f, [k]: v }; setF(n); save(n); };
  return (
    <section className="bg-white border border-slate-200/80 rounded-xl p-6 space-y-6" data-testid="automations-section">
      <h3 className="font-display font-semibold">Automatismi (ogni mattina alle 8:00)</h3>
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="font-medium text-sm">Sollecito documenti mancanti</p>
          <p className="text-sm text-slate-500 mt-1">Per le pratiche in corso (Nuovo, Istruttoria, Documenti) il cliente riceve un'email con l'elenco dei documenti mancanti e il link per caricarli. Massimo 5 solleciti per pratica.</p>
          <div className="flex items-center gap-2 mt-3 text-sm">Ogni <Input data-testid="doc-reminder-days" type="number" min={1} max={30} value={f.doc_reminder_days} onChange={(e) => setF({ ...f, doc_reminder_days: e.target.value })} onBlur={() => save()} className="w-16 h-8" /> giorni</div>
        </div>
        <Switch data-testid="doc-reminders-switch" checked={f.doc_reminders} onCheckedChange={toggle("doc_reminders")} />
      </div>
      <div className="border-t border-slate-100 pt-6 space-y-3">
        <div className="flex items-start justify-between gap-6">
          <div>
            <p className="font-medium text-sm">Auguri di compleanno automatici</p>
            <p className="text-sm text-slate-500 mt-1">Il giorno del compleanno il cliente riceve i tuoi auguri, firmati da te. Sono considerati comunicazione promozionale: partono solo ai clienti con email, data di nascita e consenso marketing attivo (firmato e non revocato).</p>
          </div>
          <Switch data-testid="birthday-emails-switch" checked={f.birthday_emails} onCheckedChange={toggle("birthday_emails")} />
        </div>
        <Textarea data-testid="birthday-message-input" rows={3} value={f.birthday_message} onChange={(e) => setF({ ...f, birthday_message: e.target.value })} />
        <p className="text-xs text-slate-400">Puoi usare {"{nome}"} e {"{cognome}"}.</p>
        <Button data-testid="save-birthday-btn" variant="outline" onClick={() => save()}>Salva messaggio</Button>
      </div>
    </section>
  );
};

export default function Settings() {
  const { user } = useAuth();
  const { data } = useApi("/settings");
  const [f, setF] = useState({ daily_summary: true, signature: "", doc_reminders: true, doc_reminder_days: 3, birthday_emails: false, birthday_message: "", ai_enabled: true, ai_include_notes: false, promo_exceptions_enabled: false });
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    if (data) setF({
      daily_summary: data.daily_summary, signature: data.signature || "", doc_reminders: data.doc_reminders ?? true,
      doc_reminder_days: data.doc_reminder_days || 3, birthday_emails: !!data.birthday_emails, birthday_message: data.birthday_message || BDAY,
      ai_enabled: data.ai_enabled ?? true,
      ai_include_notes: data.ai_include_notes === true,
      promo_exceptions_enabled: data.promo_exceptions_enabled === true,
    });
  }, [data]);

  const save = async (next = f) => {
    try { await api.put("/settings", { ...next, doc_reminder_days: Number(next.doc_reminder_days) || 3, public_base: window.location.origin }); toast.success("Impostazioni salvate"); } catch (e) { toast.error(errMsg(e)); }
  };
  const test = async () => {
    setTesting(true);
    try { await api.post("/settings/test-summary"); toast.success(`Riepilogo inviato a ${user.email}`); } catch (e) { toast.error(errMsg(e)); } finally { setTesting(false); }
  };

  return (
    <div className="space-y-6 fade-up max-w-3xl" data-testid="settings-page">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Impostazioni</h1>

      <section className="bg-white border border-slate-200/80 rounded-xl p-6 space-y-4">
        <h3 className="font-display font-semibold">Account</h3>
        <div className="flex items-center gap-4">
          <img src={user?.picture} alt="" className="h-12 w-12 rounded-full bg-slate-200" />
          <div><p className="font-medium" data-testid="settings-user-name">{user?.name}</p><p className="text-sm text-slate-500">{user?.email}</p></div>
        </div>
      </section>

      <section className="bg-white border border-slate-200/80 rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h3 className="font-display font-semibold">Riepilogo email giornaliero</h3>
            <p className="text-sm text-slate-500 mt-1">Ogni mattina alle 8:00 ricevi a <span className="font-medium">{user?.email}</span> l'elenco dei richiami del giorno e di quelli in ritardo.</p>
          </div>
          <Switch data-testid="daily-summary-switch" checked={f.daily_summary} onCheckedChange={(v) => { const n = { ...f, daily_summary: v }; setF(n); save(n); }} />
        </div>
        <Button data-testid="send-test-summary-btn" variant="outline" onClick={test} disabled={testing}>{testing ? "Invio…" : "Inviami ora un riepilogo di prova"}</Button>
      </section>

      <Automations f={f} setF={setF} save={save} />
      <AiToggle f={f} setF={setF} save={save} />
      <CalendarSection />
      <PrivacySettings data={data} />
      <DpaCard />
      <PromoLog f={f} setF={setF} save={save} />
      <DsrRegistry />
      <AccountData />

      <section className="bg-white border border-slate-200/80 rounded-xl p-6 space-y-4">
        <h3 className="font-display font-semibold">Firma email</h3>
        <p className="text-sm text-slate-500">Aggiunta in fondo alle email inviate dal CRM. Le risposte dei clienti arrivano alla tua casella Gmail.</p>
        <div className="space-y-1.5">
          <Label className="text-xs text-slate-500">Firma</Label>
          <Textarea data-testid="signature-input" rows={4} value={f.signature} onChange={(e) => setF({ ...f, signature: e.target.value })} placeholder={"Mario Bianchi\nAgente in attività finanziaria · OAM A00000\nTel. 333 1234567"} />
        </div>
        <Button data-testid="save-settings-btn" onClick={() => save()} className="bg-[#0F172A] hover:bg-sky-600">Salva firma</Button>
      </section>
    </div>
  );
}
